import React from 'react';

const STATUS_TABLE: Array<{key: string; label: string; color: string; meaning: string; whenUse: string}> = [
  { key: 'scheduled', label: 'Scheduled', color: 'bg-yellow-100 text-yellow-800',
    meaning: 'Termin wurde angelegt, aber Patient hat (noch) nicht aktiv bestätigt.',
    whenUse: 'Standard-Status. Wird automatisch gesetzt, wenn ein neuer Termin erstellt wird.' },
  { key: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-800',
    meaning: 'Patient hat den Termin bestätigt (z.B. telefonisch, per E-Mail).',
    whenUse: 'Manuell setzen, wenn ihr Bestätigung erhalten habt.' },
  { key: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800',
    meaning: 'Behandlung wurde durchgeführt.',
    whenUse: 'Nach dem Termin setzen — für Statistik und Historie.' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800',
    meaning: 'Termin wurde storniert (von Patient oder Praxis).',
    whenUse: 'Wenn der Termin nicht stattfindet. Block bleibt im Kalender sichtbar (durchgestrichen / grau in Google).' },
  { key: 'no_show', label: 'No Show', color: 'bg-gray-200 text-gray-700',
    meaning: 'Patient ist ohne Absage nicht erschienen.',
    whenUse: 'Nach dem Termin setzen, wenn Patient nicht da war — getrennt von "cancelled" für Statistik.' },
];

const HelpPage: React.FC = () => {
  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Help &amp; Workflow</h1>
        <p className="text-sm text-gray-600">Wie funktioniert das Admin-System?</p>
      </div>

      {/* Status Workflow */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">📋 Termin-Status</h2>
        <p className="text-sm text-gray-700 mb-4">
          Jeder Termin hat einen Status. Den änderst du im <strong>Calendar</strong> über das Termin-Detail-Modal
          (Klick auf den Termin) oder direkt beim Bearbeiten.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Bedeutung</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Wann setzen?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {STATUS_TABLE.map(s => (
                <tr key={s.key}>
                  <td className="px-3 py-3"><span className={`px-2 py-1 text-xs rounded-full ${s.color}`}>{s.label}</span></td>
                  <td className="px-3 py-3 text-gray-700">{s.meaning}</td>
                  <td className="px-3 py-3 text-gray-600">{s.whenUse}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* What changes happen */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🔄 Was passiert beim Status-Wechsel?</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>Status wird in der Datenbank gespeichert (sofort).</li>
          <li>Eintrag wird im <strong>Audit-Log</strong> dokumentiert (alter → neuer Status, wer, wann).</li>
          <li>Das passende <strong>Google-Calendar-Event</strong> wird aktualisiert:
            <ul className="ml-6 mt-1 space-y-1 list-circle list-inside text-gray-600">
              <li><code>cancelled</code>: Event wird in Google als <em>transparent</em> markiert (durchgestrichen/grau, blockiert keine Zeit mehr).</li>
              <li>Andere Status: Event bleibt <em>opaque</em> (normal sichtbar, Zeit ist geblockt).</li>
            </ul>
          </li>
          <li>Termin <strong>löschen</strong> (statt cancel): Eintrag wird komplett aus DB und Google Calendar entfernt.</li>
        </ul>
      </section>

      {/* Email behavior */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">✉️ Werden E-Mails verschickt?</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <strong className="text-green-900">Neu: Automatische Status-E-Mails</strong>
          </div>
          <p>E-Mails werden automatisch in folgenden Fällen an den Patienten verschickt:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Bestätigung (Confirmed):</strong> Wenn Sie den Status auf "Confirmed" setzen, erhält der Patient eine Terminbestätigung.</li>
            <li><strong>Stornierung (Cancelled):</strong> Wenn Sie den Status auf "Cancelled" setzen, erhält der Patient eine Absage.</li>
          </ul>
          <p className="mt-3">Die E-Mails werden <strong>zweisprachig</strong> versendet (in der Muttersprache des Patienten und zusätzlich auf Englisch) und enthalten alle wichtigen Termindetails im sauberen HTML-Layout.</p>
          <p className="mt-2 text-gray-600">Jeder E-Mail-Versand wird im Hintergrund geprüft. Wenn Sie im Admin eine Änderung vornehmen, sehen Sie direkt im Anschluss ein kleines grünes Popup oben rechts ("Toast"), das den erfolgreichen Versand bestätigt. Alle gesendeten E-Mails werden zudem im <strong>Audit-Log</strong> dokumentiert.</p>
        </div>
      </section>

      {/* How to change */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🛠 Wo kann ich Status ändern?</h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li><strong>Calendar:</strong> Klick auf einen Termin im Kalender → Detail-Modal öffnet sich → Status-Buttons (Confirm/Complete/Cancel/No Show).</li>
          <li><strong>Calendar (Edit):</strong> Vom Detail-Modal aus auf "Bearbeiten" klicken → im Formular Status-Feld ändern.</li>
          <li><strong>Appointments-Liste:</strong> Listet alle Termine, "Open in Calendar" Link öffnet den Tag im Kalender direkt mit Detail-Modal.</li>
        </ol>
      </section>

      {/* Google Calendar */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">📅 Google Calendar Sync</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>Termine im Admin werden automatisch in den verbundenen Google-Kalender geschrieben.</li>
          <li>Der "Sync Google Calendar" Button im Calendar-View zieht aktuelle Events zurück.</li>
          <li><strong>Pro Behandler ein eigener Kalender (empfohlen):</strong> Trag die Google Calendar ID jedes Behandlers in <em>Providers → Bearbeiten → Google Calendar ID</em> ein (z. B. <code>dra.garcia@seadent.es</code>). So werden Urlaube/private Termine, die direkt in Google eingetragen werden, automatisch als belegt erkannt und blockieren passende Slots auf seadent.es.</li>
          <li><strong>Wichtig — Freigabe für den Service-Account:</strong> Damit das funktioniert, muss jeder Provider-Kalender an den Service-Account freigegeben werden (Lese-Rechte reichen). Ohne Freigabe wird der Kalender stillschweigend als "immer frei" behandelt.</li>
        </ul>
      </section>

      {/* Online-Buchung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🌐 Online-Buchung von Patienten</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Patienten buchen auf <strong>seadent.es</strong> ihren Wunschtermin direkt im Kalender.
            Es werden nur Tage und Uhrzeiten angeboten, an denen ein passender Behandler tatsächlich frei ist.
          </p>
          <p className="font-semibold text-gray-800 mt-3">So sieht der Patient den Ablauf:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Behandlung wählen (z. B. Zahnreinigung, Beratung, Notfall …).</li>
            <li>Versicherung angeben.</li>
            <li>Im Kalender einen Tag auswählen — graue Tage sind ausgebucht oder geschlossen.</li>
            <li>Eine freie Uhrzeit anklicken → Slot wird für 5 Minuten reserviert (Countdown im Formular).</li>
            <li>Patientendaten ausfüllen und absenden.</li>
          </ol>
          <p className="mt-3">
            Die Reservierung ist eine <strong>Soft-Reservierung</strong>: Wenn der Patient nicht in 5 Minuten absendet, wird der Slot wieder freigegeben. So gehen Slots nicht "verloren", wenn Formulare offen liegen bleiben.
          </p>
          <p>
            Eingehende Online-Buchungen landen mit Status <code>pending</code> im Kalender. Du bekommst eine E-Mail an <code>info@seadent.es</code>, prüfst den Termin und setzt den Status auf <code>confirmed</code> (oder <code>cancelled</code>) — der Patient bekommt automatisch die passende E-Mail.
          </p>
        </div>
      </section>

      {/* Resource-Type-Matching */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🧑‍⚕️ Wer macht welche Behandlung? (Rollen)</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Die Online-Buchung weiß, welche Behandlung von <strong>welchem Typ Behandler</strong> erbracht wird, und zeigt nur dann freie Slots, wenn der passende Typ verfügbar ist.
          </p>

          <div className="overflow-x-auto mt-3">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Wo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Feld</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bedeutung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-3"><strong>Providers</strong></td>
                  <td className="px-3 py-3"><code>Rolle</code></td>
                  <td className="px-3 py-3">Welcher Typ ist diese Person? <em>Zahnarzt</em> oder <em>Hygiene</em>.</td>
                </tr>
                <tr>
                  <td className="px-3 py-3"><strong>Procedures</strong></td>
                  <td className="px-3 py-3"><code>Benötigte Rollen</code></td>
                  <td className="px-3 py-3">Welche Rolle(n) müssen für diese Behandlung frei sein? Standard: Zahnreinigung → Hygiene; alles andere → Zahnarzt.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-3 font-semibold text-gray-800">Beispiel:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Patient möchte eine <em>Zahnreinigung</em> am Dienstag um 10:00.</li>
            <li>Die Behandlung verlangt Rolle <em>Hygiene</em>.</li>
            <li>Die Hygienikerin ist um 10:00 frei → Slot wird angeboten ✅</li>
            <li>Wäre der Zahnarzt um 10:00 frei, aber die Hygienikerin nicht → Slot wäre <strong>nicht</strong> verfügbar.</li>
          </ul>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mt-3">
            <strong className="text-yellow-900">Was du tun musst:</strong>
            <ul className="mt-1 list-disc list-inside space-y-1 text-yellow-900">
              <li>Bei jedem Behandler in <em>Providers</em> die <strong>Rolle</strong> setzen (Zahnarzt oder Hygiene).</li>
              <li>Bei jeder Behandlung in <em>Procedures</em> die <strong>Benötigten Rollen</strong> prüfen — die Defaults passen für die meisten Fälle.</li>
              <li>Optional bei jedem Behandler die <strong>Google Calendar ID</strong> hinterlegen, damit Urlaubs-/Privattermine aus Google die Online-Slots blockieren.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stammdaten */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">⚙️ Stammdaten verwalten</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li><strong>Providers:</strong> Ärzte/Hygieniker — Name, Email, Telefon, <strong>Rolle</strong> (Zahnarzt/Hygiene), <strong>Google Calendar ID</strong> (optional). Inaktive werden in den Termin-Dropdowns und der Online-Buchung ausgeblendet.</li>
          <li><strong>Procedures:</strong> Behandlungen — Name (mehrsprachig), Beschreibung, Dauer (Minuten), Farbe, <strong>Benötigte Rollen</strong>, sowie der Schalter "Bookable on public site" (steuert, ob die Behandlung auf seadent.es überhaupt angeboten wird).</li>
          <li><strong>Operatories:</strong> Behandlungsräume — Name, Beschreibung. Wird beim Erstellen eines Termins ausgewählt (Konflikt-Check pro Raum).</li>
          <li>Beim Löschen oder Deaktivieren wird der Eintrag aus den Dropdowns ausgeblendet, aber bestehende Termine bleiben erhalten.</li>
        </ul>
      </section>

      {/* Powered by LEANOFY */}
      <footer className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
        Powered by{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-navy-admin hover:underline"
        >
          LEANOFY
        </a>
        {' · '}
        Support &amp; Anfragen:{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="text-navy-admin hover:underline"
        >
          www.leanofy.de
        </a>
      </footer>
    </div>
  );
};

export default HelpPage;
